import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@material-ui/core';
import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Stars as StarsIcon,
  FlashOn as FlashOnIcon,
  ArrowForward as ArrowForwardIcon,
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  StoreMallDirectory as StoreIcon,
  LocalBar as BarIcon,
  Face as BeautyIcon,
  ShoppingCart as MarketIcon,
  Build as ToolsIcon,
  BusinessCenter as GeneralIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpIcon,
  MenuBook as MenuBookIcon,
  InfoOutlined as InfoIcon,
  AssignmentOutlined as TasksIcon
} from '@material-ui/icons';

import StoreContext from '../Store/context';
import useApi from '../../utils/useApi';
import { updateCompanySegment } from '../../services/companyService';
import { SEGMENTS_DATA, STEP_GUIDES, normalizeSegment } from './trialOnboardingData';
import './TrialOnboardingGuide.css';

const ICON_COMPONENTS = {
  StoreMallDirectory: StoreIcon,
  LocalBar: BarIcon,
  Face: BeautyIcon,
  ShoppingCart: MarketIcon,
  Build: ToolsIcon,
  BusinessCenter: GeneralIcon
};

const TrialOnboardingGuide = ({ onDismiss }) => {
  const history = useHistory();
  const { credencials, openChatbotWithPrompt, setCredencials } = useContext(StoreContext);

  const companyId = credencials?.company || credencials?.companyData?.id || 'default';
  const initialSegmentFromCreds = normalizeSegment(
    credencials?.companyData?.segment || credencials?.segment
  );

  // Selected Segment State (persisted per company)
  const [selectedSegmentKey, setSelectedSegmentKey] = useState(() => {
    const saved = localStorage.getItem(`duzz_onboarding_seg_${companyId}`);
    return saved && SEGMENTS_DATA[saved] ? saved : initialSegmentFromCreds;
  });

  // Completed steps map: { [stepId]: boolean }
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      const saved = localStorage.getItem(`duzz_onboarding_progress_${companyId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Minimized / Collapsed state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem(`duzz_onboarding_collapsed_${companyId}`) === 'true';
  });

  // Dismissed state (if 100% completed or user closes)
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(`duzz_onboarding_dismissed_${companyId}`) === 'true';
  });

  // Catalog Addon Active state (only show catalog step if company has the catalog addon)
  const [hasCatalogAddon, setHasCatalogAddon] = useState(() => {
    const fromCreds = credencials?.companyData?.addon_ids?.includes('catalog') ||
      credencials?.companyData?.has_catalog_addon;
    return Boolean(fromCreds);
  });

  // Segment Switcher Menu Anchor
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // Active Guide Step (Modal)
  const [activeGuideStep, setActiveGuideStep] = useState(null);

  // API Call for Onboarding Status
  const [fetchStatus] = useApi({
    method: 'get',
    url: '/onboarding/status',
  });

  const lastFetchRef = useRef(0);
  const isFetchingRef = useRef(false);

  // Auto-detect completed steps with 30s cooldown
  const checkAutoProgress = useCallback(async (force = false) => {
    if (isDismissed) return;
    const now = Date.now();
    if (!force && now - lastFetchRef.current < 30000) return;
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    lastFetchRef.current = now;

    try {
      const res = await fetchStatus();
      if (res && res.data) {
        const d = res.data;
        if (d.has_catalog_addon !== undefined) {
          setHasCatalogAddon(Boolean(d.has_catalog_addon));
        }

        const autoChecked = {
          categories: d.categories_count > 0,
          products: d.products_count > 0,
          stock_drinks: d.products_count > 0,
          resale_products: d.products_count > 0,
          parts_stock: d.products_count > 0,
          xml_import: d.products_count > 0,
          services: d.services_count > 0,
          services_combos: Boolean(d.has_service_combos),
          services_labor: d.services_count > 0,
          attendants: d.users_count > 0,
          waiter_user: d.users_count > 0,
          first_client: d.customers_count > 0,
          cashier_management: d.cash_registers_count > 0,
          first_sale: d.sales_count > 0,
          quick_pos: d.sales_count > 0,
          first_comanda: d.sales_sessions_count > 0,
          budget_comanda: d.sales_sessions_count > 0,
          catalog: Boolean(d.catalog_enabled),
          agenda_setup: d.schedules_count > 0,
          delivery_settings: Boolean(d.has_delivery_tax),
          duxia_customer_analysis: Boolean(d.has_customer_analysis),
          duxia_product_description: Boolean(d.has_product_description),
          duxia_brand_palette: Boolean(d.has_brand_palette),
          duxia_eval: Boolean(d.has_chat_message),
        };

        setCompletedSteps((prev) => ({
          ...prev,
          ...autoChecked,
        }));
      }
    } catch (e) {
      // fail silently without disruption
    } finally {
      isFetchingRef.current = false;
    }
  }, [fetchStatus, isDismissed]);

  // Initial and window focus check
  useEffect(() => {
    checkAutoProgress();
    const handleFocus = () => checkAutoProgress();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkAutoProgress]);

  // Current Segment Configuration (Filtered by required addons)
  const segmentConfig = useMemo(() => {
    const raw = SEGMENTS_DATA[selectedSegmentKey] || SEGMENTS_DATA['Outro'];
    const filteredSteps = raw.steps.filter((s) => {
      if ((s.requireAddon === 'catalog' || s.id === 'catalog') && !hasCatalogAddon) {
        return false;
      }
      return true;
    });
    return {
      ...raw,
      steps: filteredSteps,
    };
  }, [selectedSegmentKey, hasCatalogAddon]);

  // Icon for the segment
  const SegmentIcon = ICON_COMPONENTS[segmentConfig.icon] || GeneralIcon;

  // Calculate Progress
  const totalSteps = segmentConfig.steps.length;
  const completedCount = segmentConfig.steps.filter((s) => completedSteps[s.id]).length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;
  const isAllDone = completedCount === totalSteps && totalSteps > 0;

  // Persist completed steps and broadcast update
  useEffect(() => {
    try {
      localStorage.setItem(
        `duzz_onboarding_progress_${companyId}`,
        JSON.stringify(completedSteps)
      );
      window.dispatchEvent(
        new CustomEvent('duzz:onboarding_progress_updated', {
          detail: { companyId, completedSteps }
        })
      );
    } catch (e) { }
  }, [completedSteps, companyId]);

  // Listen for progress and segment updates from modal
  useEffect(() => {
    const handleProgressUpdate = (e) => {
      if (e?.detail?.companyId === companyId && e?.detail?.completedSteps) {
        setCompletedSteps((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(e.detail.completedSteps)) return prev;
          return e.detail.completedSteps;
        });
      }
    };
    const handleSegmentUpdate = (e) => {
      const incomingCompany = e?.detail?.companyId;
      const seg = e?.detail?.segmentKey || e?.detail?.segment;
      if ((!incomingCompany || String(incomingCompany) === String(companyId)) && seg) {
        setSelectedSegmentKey((prev) => {
          const norm = normalizeSegment(seg);
          if (prev === norm) return prev;
          return norm;
        });
      }
    };
    window.addEventListener('duzz:onboarding_progress_updated', handleProgressUpdate);
    window.addEventListener('duzz:onboarding_seg_updated', handleSegmentUpdate);
    return () => {
      window.removeEventListener('duzz:onboarding_progress_updated', handleProgressUpdate);
      window.removeEventListener('duzz:onboarding_seg_updated', handleSegmentUpdate);
    };
  }, [companyId]);

  // Persist segment selection
  const handleSelectSegment = (segmentKey) => {
    setSelectedSegmentKey(segmentKey);
    localStorage.setItem(`duzz_onboarding_seg_${companyId}`, segmentKey);
    window.dispatchEvent(
      new CustomEvent('duzz:onboarding_seg_updated', {
        detail: { companyId, segmentKey }
      })
    );
    setMenuAnchorEl(null);
    updateCompanySegment(companyId, segmentKey, credencials, setCredencials);
  };

  // Toggle step completion
  const handleToggleStep = (stepId, e) => {
    if (e) e.stopPropagation();
    setCompletedSteps((prev) => {
      const next = { ...prev, [stepId]: !prev[stepId] };
      return next;
    });
  };

  // Execute Step Action
  const handleStepAction = (step) => {
    if (step.type === 'duxia' && step.prompt) {
      if (typeof openChatbotWithPrompt === 'function') {
        openChatbotWithPrompt(step.prompt);
      }
    } else if (step.route) {
      history.push(step.route);
    }
  };

  // Toggle Collapsed
  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(`duzz_onboarding_collapsed_${companyId}`, String(next));
      return next;
    });
  };

  // Dismiss Guide
  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(`duzz_onboarding_dismissed_${companyId}`, 'true');
    if (typeof onDismiss === 'function') {
      onDismiss();
    }
  };

  // Split steps into pending and completed
  const pendingSteps = useMemo(
    () => segmentConfig.steps.filter((s) => !completedSteps[s.id]),
    [segmentConfig.steps, completedSteps]
  );

  const doneSteps = useMemo(
    () => segmentConfig.steps.filter((s) => Boolean(completedSteps[s.id])),
    [segmentConfig.steps, completedSteps]
  );

  const renderStepCard = (step, index, isDone = false) => {
    const isDuxia = step.type === 'duxia' || step.type === 'duxia_feature';

    return (
      <Box
        key={step.id}
        className={`trial-onboarding-step-card ${isDone ? 'trial-onboarding-step-card--done' : ''} ${isDuxia && !isDone ? 'trial-onboarding-step-card--duxia' : ''}`}
        onClick={() => setActiveGuideStep(step)}
        title="Clique para ver o guia passo a passo desta missão"
      >
        <Box className="trial-onboarding-step-left">
          <div
            className={`trial-onboarding-checkbox ${isDone ? 'trial-onboarding-checkbox--checked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleStep(step.id, e);
            }}
            title={isDone ? "Marcar como pendente" : "Marcar como concluído"}
          >
            {isDone && <CheckIcon style={{ fontSize: 15 }} />}
          </div>

          <Box className="trial-onboarding-step-text">
            <Typography
              variant="subtitle2"
              className={`trial-onboarding-step-title ${isDone ? 'trial-onboarding-step-title--done' : ''}`}
            >
              {!isDone && typeof index === 'number' ? `${index + 1}. ` : ''}{step.title}
              {isDuxia && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 10,
                    background: isDone ? '#f1f5f9' : '#f3e8ff',
                    color: isDone ? '#64748b' : '#7c3aed',
                    fontWeight: 800,
                    letterSpacing: 0.5
                  }}
                >
                  <ChatIcon style={{ fontSize: 10 }} /> DuxIA
                </span>
              )}
            </Typography>
            <Typography variant="body2" className="trial-onboarding-step-desc">
              {step.description}
            </Typography>
          </Box>
        </Box>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleStepAction(step);
          }}
          className={`trial-onboarding-action-btn ${isDone ? 'trial-onboarding-action-btn--done' : ''} ${isDuxia && !isDone ? 'trial-onboarding-action-btn--duxia' : ''}`}
        >
          <span>{isDone ? 'Revisar' : step.actionText}</span>
          {isDuxia ? <ChatIcon style={{ fontSize: 14 }} /> : <ArrowForwardIcon style={{ fontSize: 14 }} />}
        </button>
      </Box>
    );
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Box className={`trial-onboarding-guide ${isAllDone ? 'trial-onboarding-guide--completed' : ''}`}>
      <div className="trial-onboarding-guide__glow" />

      {/* Header Top Bar: Badges + Controls */}
      <Box className="trial-onboarding-topbar">
        <Box className="trial-onboarding-badges">
          <button
            type="button"
            className="trial-onboarding-tag trial-onboarding-tag--clickable"
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            title="Clique para trocar de nicho"
            style={{
              backgroundColor: segmentConfig.badgeBg,
              color: segmentConfig.badgeColor,
              border: `1px solid ${segmentConfig.badgeColor}40`
            }}
          >
            <SegmentIcon style={{ fontSize: 13 }} />
            <span>{segmentConfig.tag}</span>
            <ExpandMoreIcon style={{ fontSize: 13, marginLeft: 2, opacity: 0.8 }} />
          </button>
        </Box>

        {/* Right Controls */}
        <Box className="trial-onboarding-controls">
          <Tooltip title={isCollapsed ? "Expandir Checklist" : "Minimizar"}>
            <button
              type="button"
              className="trial-onboarding-icon-btn"
              onClick={toggleCollapsed}
            >
              {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </button>
          </Tooltip>

          <Tooltip title="Ocultar Guia">
            <button
              type="button"
              className="trial-onboarding-icon-btn"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="small" />
            </button>
          </Tooltip>
        </Box>
      </Box>

      {/* Title Section */}
      <Box className="trial-onboarding-title-wrapper">
        <Typography variant="h6" className="trial-onboarding-title">
          Primeiros Passos
        </Typography>
        <Typography variant="body2" className="trial-onboarding-subtitle">
          {segmentConfig.headline}
        </Typography>
      </Box>

      {/* Segment Selector Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => setMenuAnchorEl(null)}
        keepMounted
      >
        {Object.entries(SEGMENTS_DATA).map(([key, config]) => {
          const ItemIcon = ICON_COMPONENTS[config.icon] || GeneralIcon;
          return (
            <MenuItem
              key={key}
              selected={key === selectedSegmentKey}
              onClick={() => handleSelectSegment(key)}
              style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px' }}
            >
              <ItemIcon style={{ fontSize: 18, color: config.badgeColor }} />
              <span style={{ fontWeight: key === selectedSegmentKey ? 700 : 500 }}>
                {config.label}
              </span>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Progress Bar Section */}
      <Box className="trial-onboarding-progress-wrapper">
        <Box className="trial-onboarding-progress-info">
          <span className="trial-onboarding-progress-label">
            <StarsIcon style={{ fontSize: 16, color: '#f59e0b' }} />
            Progresso de Configuração
          </span>
          <span className="trial-onboarding-progress-pct">
            {completedCount} de {totalSteps} concluídos ({progressPercent}%)
          </span>
        </Box>
        <Box className="trial-onboarding-progress-bar">
          <div
            className="trial-onboarding-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </Box>
      </Box>

      {/* Collapsed Active Objective View */}
      {isCollapsed && (
        <>
          {(() => {
            const currentStep = segmentConfig.steps.find((s) => !completedSteps[s.id]);
            if (currentStep) {
              const isDuxia = currentStep.type === 'duxia' || currentStep.type === 'duxia_feature';
              return (
                <Box
                  className="trial-onboarding-collapsed-card"
                  onClick={() => handleStepAction(currentStep)}
                >
                  <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 4 }}>
                    <span className="trial-onboarding-collapsed-bullet" />
                    <span className="trial-onboarding-collapsed-label">
                      OBJETIVO ATUAL
                    </span>
                  </Box>

                  <Typography variant="subtitle2" className="trial-onboarding-collapsed-title">
                    {currentStep.title}
                  </Typography>
                  <Typography variant="caption" className="trial-onboarding-collapsed-desc">
                    {currentStep.description}
                  </Typography>

                  <button
                    type="button"
                    className={`trial-onboarding-collapsed-action-btn ${isDuxia ? 'trial-onboarding-action-btn--duxia' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStepAction(currentStep);
                    }}
                  >
                    <span>{currentStep.actionText}</span>
                    {isDuxia ? <ChatIcon style={{ fontSize: 13 }} /> : <ArrowForwardIcon style={{ fontSize: 13 }} />}
                  </button>
                </Box>
              );
            }

            if (isAllDone) {
              return (
                <Box className="trial-onboarding-collapsed-card trial-onboarding-collapsed-card--done" onClick={toggleCollapsed}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <CheckCircleIcon style={{ color: '#10b981', fontSize: 18 }} />
                    <Typography variant="body2" style={{ color: '#065f46', fontWeight: 700, fontSize: '0.82rem' }}>
                      Todos os passos concluídos!
                    </Typography>
                  </Box>
                </Box>
              );
            }
            return null;
          })()}
        </>
      )}

      {/* Collapsible Steps List */}
      <Collapse in={!isCollapsed}>
        <Box className="trial-onboarding-steps">
          {/* Pendentes */}
          {pendingSteps.map((step, index) => renderStepCard(step, index, false))}

          {/* Seção de Concluídos */}
          {doneSteps.length > 0 && (
            <Box className="trial-onboarding-completed-section">
              <Box className="trial-onboarding-completed-header">
                <CheckCircleIcon style={{ fontSize: 14, color: '#10b981' }} />
                <span>Concluídos ({doneSteps.length})</span>
              </Box>
              <Box className="trial-onboarding-completed-list">
                {doneSteps.map((step) => renderStepCard(step, null, true))}
              </Box>
            </Box>
          )}
        </Box>

        {/* 100% Celebration Banner */}
        {isAllDone && (
          <Box className="trial-onboarding-celebration">
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <CheckCircleIcon style={{ color: '#10b981', fontSize: 20 }} />
              <span className="trial-onboarding-celebration__text">
                Parabéns! Você concluiu todos os cadastros essenciais do seu negócio.
              </span>
            </Box>
            <button
              type="button"
              className="trial-onboarding-segment-btn"
              onClick={handleDismiss}
              style={{ backgroundColor: '#d1fae5', borderColor: '#a7f3d0', color: '#065f46' }}
            >
              Ocultar
            </button>
          </Box>
        )}
      </Collapse>

      {/* Mission Guide Dialog */}
      <Dialog
        open={Boolean(activeGuideStep)}
        onClose={() => setActiveGuideStep(null)}
        maxWidth="sm"
        fullWidth
        className="trial-onboarding-guide-modal"
      >
        {activeGuideStep && (() => {
          const guide = STEP_GUIDES[activeGuideStep.id] || {
            headline: activeGuideStep.title,
            whyItMatters: activeGuideStep.description,
            steps: [
              `Clique no botão "${activeGuideStep.actionText}" para acessar a tela correspondente.`,
              "Complete o preenchimento dos dados no sistema.",
              "Salve para registrar a evolução."
            ],
            tip: "Mantenha seus dados organizados para agilizar suas vendas diárias."
          };
          const isDone = Boolean(completedSteps[activeGuideStep.id]);
          const isDuxia = activeGuideStep.type === 'duxia' || activeGuideStep.type === 'duxia_feature';

          return (
            <>
              <DialogTitle className="trial-onboarding-dialog-title" disableTypography>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                    <span className="trial-onboarding-dialog-icon">
                      <MenuBookIcon style={{ fontSize: 18, color: '#17497f' }} />
                    </span>
                    <Box>
                      <Typography variant="subtitle1" style={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                        {activeGuideStep.title}
                      </Typography>
                      <Typography variant="caption" style={{ color: '#64748b' }}>
                        Guia prático da missão
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setActiveGuideStep(null)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent dividers className="trial-onboarding-dialog-content">
                {/* Why it matters */}
                <Box className="trial-onboarding-dialog-why">
                  <span className="trial-onboarding-dialog-why-title">Objetivo</span>
                  <Typography variant="body2" style={{ color: '#334155', marginTop: 3, fontSize: '0.88rem', lineHeight: 1.5 }}>
                    {guide.whyItMatters}
                  </Typography>
                </Box>

                {/* Steps List */}
                <Box className="trial-onboarding-dialog-steps-section">
                  <Typography variant="subtitle2" style={{ fontWeight: 800, color: '#0f172a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TasksIcon style={{ fontSize: 16, color: '#17497f' }} />
                    Como Fazer (Passo a Passo)
                  </Typography>
                  <Box className="trial-onboarding-dialog-steps-list">
                    {guide.steps.map((st, idx) => (
                      <Box key={idx} className="trial-onboarding-dialog-step-item">
                        <span className="trial-onboarding-dialog-step-num">{idx + 1}</span>
                        <Typography variant="body2" style={{ color: '#1e293b', fontSize: '0.85rem', lineHeight: 1.5 }}>
                          {st}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Tip */}
                {guide.tip && (
                  <Box className="trial-onboarding-dialog-tip">
                    <InfoIcon style={{ fontSize: 18, color: '#b45309', marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="caption" style={{ fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Recomendação
                      </Typography>
                      <Typography variant="body2" style={{ color: '#78350f', fontSize: '0.82rem', marginTop: 2, lineHeight: 1.4 }}>
                        {guide.tip}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </DialogContent>

              <DialogActions className="trial-onboarding-dialog-actions">
                <Button
                  onClick={() => {
                    handleToggleStep(activeGuideStep.id);
                    setActiveGuideStep(null);
                  }}
                  color="default"
                  size="small"
                  style={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                >
                  {isDone ? "Desmarcar conclusão" : "Marcar como concluído"}
                </Button>

                <button
                  type="button"
                  className={`trial-onboarding-dialog-cta-btn ${isDuxia ? 'trial-onboarding-action-btn--duxia' : ''}`}
                  onClick={() => {
                    const stepToRun = activeGuideStep;
                    setActiveGuideStep(null);
                    handleStepAction(stepToRun);
                  }}
                >
                  <span>{activeGuideStep.actionText || 'Fazer Agora'}</span>
                  {isDuxia ? <ChatIcon style={{ fontSize: 14 }} /> : <ArrowForwardIcon style={{ fontSize: 14 }} />}
                </button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default TrialOnboardingGuide;
